import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = guest, obj = signed in
  const [error, setError] = useState("");
  // SEND-191 ITEM 3 (Howard ruled 2026-08-14) — THE STATUS TRAVELS WITH THE
  // MESSAGE. A 429 lockout and a 401 rejection arrived here as the same
  // grey string, so "locked for N minutes" read as "wrong password" and the
  // next five attempts were spent on a problem that had already stopped
  // being the problem. The screen needs to know WHICH, so the status comes
  // with it. Nothing about the request, the token or the limiter changes.
  const [errorStatus, setErrorStatus] = useState(0);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    setErrorStatus(0);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setErrorStatus(e.response?.status || 0);
      return false;
    }
  };

  const register = async (email, password, name, company_name, invite_code, signup_code) => {
    setError("");
    setErrorStatus(0);
    try {
      const { data } = await api.post("/auth/register", {
        email, password, name, company_name, invite_code, signup_code,
      });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setErrorStatus(e.response?.status || 0);
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Server-side logout best-effort; clear local state regardless
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("Logout call failed; clearing local session anyway", e);
      }
    }
    setUser(false);
  };

  const value = useMemo(
    () => ({ user, error, errorStatus, login, register, logout, refreshMe: fetchMe }),
    [user, error, errorStatus, fetchMe]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
